// NotificationContext.js — powers the bell badge in Navbar.
//
// One shared count (not per-page state) so it stays in sync everywhere:
// polls periodically, and any page that marks messages read (currently
// just Messages.js, via fetchConversationMessages) can call
// refreshUnreadCount() to reflect the drop immediately instead of waiting
// for the next poll. Same pattern as the mobile app's NotificationContext.
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { fetchUnreadMessageCount } from "../api/api";
import { useAuth } from "./AuthContext";

const NotificationContext = createContext({ unreadCount: 0, refreshUnreadCount: () => {} });

const POLL_INTERVAL_MS = 30000;

export function NotificationProvider({ children }) {
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const intervalRef = useRef(null);

  const refreshUnreadCount = useCallback(async () => {
    if (!user) return;
    try {
      const count = await fetchUnreadMessageCount();
      setUnreadCount(count);
    } catch (err) {
      console.log("[NotificationContext] unread-count fetch failed:", err.response?.data || err.message);
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      setUnreadCount(0);
      return;
    }
    refreshUnreadCount();
    intervalRef.current = setInterval(refreshUnreadCount, POLL_INTERVAL_MS);
    return () => clearInterval(intervalRef.current);
  }, [user, refreshUnreadCount]);

  return (
    <NotificationContext.Provider value={{ unreadCount, refreshUnreadCount }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  return useContext(NotificationContext);
}
